<template>
  <div v-if="skuId" class="product-delivery">
    <product-delivery-item
      title="Забрать из магазина"
      :description="availabilityText"
      :icon="$options.icons.IconPoint.id"
      :disabled="!isAvailability"
      @click.native="onClick(deliveryTypes.availability)"
    />

    <product-delivery-item
      title="Доставить курьером"
      :description="deliveryText"
      :icon="$options.icons.IconBox.id"
      :disabled="!isDelivery"
      @click.native="onClick(deliveryTypes.delivery)"
    />

    <product-delivery-item
      title="Забрать из пункта выдачи"
      :description="postamatText"
      :icon="$options.icons.IconPostamat.id"
      :disabled="!isPostamat"
      @click.native="onClick(deliveryTypes.postamat)"
    />
  </div>
</template>

<script>
import IconBox from '@icons/box.svg';
import IconPoint from '@icons/point.svg';
import IconPostamat from '@icons/postamat.svg';
import { DELIVERY_TYPES } from '@/constants/delivery';
import { getDeliveryInterval } from '@/utils/delivery-utils';
import { TextBuilder } from '@/utils/text-builder';
import { pluralize } from '@/utils/string-utils';
import { ddmmyyyy } from '@/utils/date-to-string';
import ProductDeliveryItem from '@/components/product/product-delivery-item.vue';

export default {
  name: 'product-delivery',

  icons: {
    IconBox,
    IconPoint,
    IconPostamat,
  },

  components: { ProductDeliveryItem },

  props: {
    activeSku: {
      type: Object,
      default: () => ({}),
    },
    availabilityStores: {
      type: Array,
      default: () => ([]),
    },
  },

  computed: {
    deliveryTypes: () => DELIVERY_TYPES,

    skuId() {
      return this.activeSku?.id;
    },

    availabilityInfo() {
      return this.availabilityStores.reduce((result, { availableSkus }) => {
        availableSkus?.forEach(({ id, delivery }) => {
          // обрабатываем магазины только с выбранным sku
          if (id !== this.skuId) return;

          // если есть минимум 1 магазин с предоплатой
          if (delivery?.needPrepay) result.isNeedPrepay = true;

          // если есть delayed, то увеличиваем кол-во магазинов "под заказ", иначе "в наличии"
          delivery?.delayed ? result.delayedCount++ : result.storesCount++;

          // выбираем самую ближайшую дата доставки
          if (
            delivery?.nearestDate
            && (!result.nearestDate || new Date(delivery.nearestDate) < new Date(result.nearestDate))
          ) {
            result.nearestDate = delivery.nearestDate;
          }
        });

        return result;
      }, {
        storesCount: 0,
        delayedCount: 0,
        isNeedPrepay: false,
        nearestDate: '',
      });
    },

    storesCount() { // кол-во магазинов "в наличии"
      return this.availabilityInfo.storesCount;
    },

    delayedCount() { // кол-во магазинов "под заказ"
      return this.availabilityInfo.delayedCount;
    },

    nearestDate() { // ближайшая дата доставки в формате dd.mm.yyyy
      const { nearestDate } = this.availabilityInfo;
      return nearestDate ? ddmmyyyy(new Date(nearestDate)) : '';
    },

    isAvailability() {
      return !!this.availabilityCount;
    },

    availabilityCount() {
      return this.storesCount || this.delayedCount;
    },

    delivery() {
      return this.activeSku?.delivery || {};
    },

    isDelivery() {
      return !!this.delivery?.interval;
    },

    deliveryInterval() {
      if (!this.isDelivery) return '';
      return getDeliveryInterval(this.delivery.interval);
    },

    availabilityText() {
      if (!this.isAvailability) return '';

      const textBuilder = new TextBuilder('Бесплатно');
      const isInStores = !!this.storesCount;
      const isDelayed = !!this.delayedCount;
      const { isNeedPrepay } = this.availabilityInfo;

      textBuilder
        .addStyledText(' сегодня', { color: '#1a82cb' }, isInStores)
        .addText(' в', isInStores)
        .addStyledText(
          ` ${pluralize(this.storesCount, ['магазине', 'магазинах', 'магазинах'])}`,
          { color: '#333' },
          isInStores,
        )
        .addText(' или', isInStores && isDelayed)
        .addText(` под&nbsp;заказ`, isDelayed)
        .addText(' с', !!(isDelayed && this.nearestDate))
        .addStyledText(` ${this.nearestDate}`, { color: '#1a82cb' }, !!(isDelayed && this.nearestDate))
        .addText(' в', isDelayed)
        .addStyledText(` ${pluralize(this.delayedCount, ['магазине', 'магазинах', 'магазинах'])}`, { color: '#333' }, isDelayed)
        .addText(' при 100% предоплате', isNeedPrepay);

      return textBuilder.toString();
    },

    deliveryText() {
      if (!this.isDelivery) return '';

      const limit = this.delivery?.freeShippingLimit;
      const { max } = this.delivery?.interval || {};
      // const { isNeedPrepay } = this.availabilityInfo;
      const textBuilder = new TextBuilder('Бесплатно');

      textBuilder
        .addStyledText(` за ${this.deliveryInterval} ${pluralize(max, ['день', 'дня', 'дней'], true)}`, { color: '#1a82cb' })
        .addText(` при покупке от&nbsp;${this.$options.filters.price(limit)}&nbsp;₽`, !!limit);
        // .addText(' при 100% предоплате', isNeedPrepay); // todo ESMFUN-724 - пока выключить

      return textBuilder.toString();
    },

    isPostamat() {
      return !!this.activeSku?.pickup?.thirdParty;
    },

    postamatText() {
      if (!this.isPostamat) return '';

      const { freeShippingLimit } = this.delivery;
      const textBuilder = new TextBuilder('Бесплатно');

      // если лимит задан и равен 0, то возвращаем бесплатно
      if (freeShippingLimit && +freeShippingLimit === 0) return textBuilder.toString();

      textBuilder
        .addText(` при покупке от&nbsp;${this.$options.filters.price(freeShippingLimit || 2000)}&nbsp;₽`)
        .addText('*', !freeShippingLimit); // если лимит не задан, добавляем *

      return textBuilder.toString();
    },
  },

  methods: {
    onClick(name) {
      this.$emit('click', {
        name,
        deliveryInterval: this.deliveryInterval,
        storesCount: this.availabilityCount,
      });
    },
  },
};
</script>

<style lang="scss">
.product-delivery {
  border: 1px solid $gray_lu;
  border-radius: 4px;
}
</style>
